import { useStreamV2Orders } from '@hooks/useStreamV2Orders';
import { Order, OrderType, OrderState } from '@protos/v2/order';
import { BodyScrollEndEvent, ColDef, GridApi, GridReadyEvent, IRowNode } from 'ag-grid-enterprise';
import { createRef, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact, CustomHeaderProps } from 'ag-grid-react';
import { StyledBox } from '@shared/components/TextInput';
import { useAppStore, useWidgetStore } from '@features/common/store/AppStore';
import { useStreamV2Status } from '@hooks/useStreamV2Status';
import { useDataLoader } from '@hooks/useDataLoader';

import {
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  SelectChangeEvent,
  FormControlLabel,
  SelectProps,
  FormControlProps,
  Checkbox,
  Button,
  ButtonProps,
  Typography,
} from '@mui/material';
import { getDefaultTabName, OrderTrackerStore } from './store/OrderTrackerStore';
import { OrderTrackerOrderState, OrderTrackerOrderType, OrderTrackerQuickView, OrderTrackerTabData } from './store/OrderTrackerTypes';
import { ProductAutocomplete } from '@features/common/components/ProductAutocomplete';
import { Product } from '@protos/product';
import { DataApi } from '@utils/data';
import { IsolatedTextField } from '@shared/components/TradingOrdersGrid/IsolatedTextField';
import { useOrderDetailsPanel } from '@shared/components/OrderDetailsPanel/useOrderDetailsPanel';
import { createOrderDetailsSidebarConfig, createOrderPanelDetails } from '@shared/components/OrderDetailsPanel/orderDetailsUtils';

type OrderTrackerTabProps = {
  id: string;
  columns: ColDef[];
  index: number;
  handleTabUpdate: (tabIndex: number, data: Partial<OrderTrackerTabData>) => void;
};

const defaultColDef: ColDef<Order> = {
  suppressMovable: true,
  cellStyle: {},
  headerComponent: (props: CustomHeaderProps<Order>) => {
    return <div style={{ paddingBottom: '2px' }}>{props.displayName.replaceAll('_', ' ').toUpperCase()}</div>;
  },
  minWidth: 100,
  flex: 1,
  resizable: true,
};

const QuickButtonDefaultProps: Partial<ButtonProps> = {
  variant: 'contained',
  size: 'small',
  color: 'secondary',
  sx: { transition: 'all 0.1s ease' },
};

const OrderTrackerTabContent = (props: OrderTrackerTabProps & { width: number }) => {
  const { id, width, columns, index, handleTabUpdate } = props;

  const gridApiRef = useRef<GridApi<Order> | null>(null);
  const { tabs, tradingAccounts, updateTab } = useWidgetStore<OrderTrackerStore>(id);
  const tabData: OrderTrackerTabData = tabs[props.index];

  const { orderType: orderTypeFilter, orderState: orderStateFilter, productSymbol, showUserOrdersOnly, tradingAccount, tabName } = tabData;

  const { user, apiClient, products } = useAppStore();

  // State
  const [gridReady, setGridReady] = useState<boolean>(false);
  const [needsData, setNeedsData] = useState<boolean>(true);

  // Get the actual product from the list (assuming there is one)
  const selectedProduct: Product | null = useMemo(() => {
    return products?.find(p => p.symbol === productSymbol?.toLowerCase()) || null;
  }, [products, productSymbol]);

  const status = useStreamV2Status();

  const { selectedRow, visiblePanel, onRowClicked, onCellKeyDown, onRowDoubleClicked, onToolPanelVisibilityChanged } = useOrderDetailsPanel({
    apiClient: apiClient!,
  });

  const onGridReady = (e: GridReadyEvent<Order>) => {
    gridApiRef.current = e.api;
    setGridReady(true);
  };

  const onStreamOrder = (order: Order) => {
    if (gridApiRef.current) {
      const api = gridApiRef.current;

      let canUpdate: boolean = true;

      // Check user id
      if (showUserOrdersOnly && order.user.id !== user?.id) {
        canUpdate = false;
      }

      // Symbol
      if (tabData.productSymbol && tabData.productSymbol !== 'all' && order.product_symbol !== tabData.productSymbol) {
        canUpdate = false;
      }

      // Order type (possibly already handled by API)
      if (tabData.orderType !== 'all' && tabData.orderType !== order.order_type) {
        canUpdate = false;
      }

      // Order state
      if (!tabData.orderState.includes('all')) {
        if (!(tabData.orderState as string[]).includes(order.order_state)) {
          canUpdate = false;
        }
      }

      // Trading account
      if (tabData.tradingAccount !== 'all') {
        if (tabData.tradingAccount !== order.account_id) return;
      }

      // If it's there, decide if we should update it or remove it
      if (api.getRowNode(order.id)) {
        if (canUpdate) {
          api.applyTransaction({ update: [order] });
        } else {
          api.applyTransaction({ remove: [order] });
        }
      }
      // If it's not there, only add it if valid
      else if (canUpdate) {
        api.applyTransaction({ add: [order] });
      }
    }
  };

  const allowedOrderTypes: (OrderType | 'all')[] = ['all', OrderType.LIMIT, OrderType.FILL_OR_KILL];
  const allowedOrderStates: (OrderState | 'all')[] = [
    'all',
    OrderState.NEW,
    OrderState.PARTIALLY_FILLED,
    OrderState.FILLED,
    OrderState.CANCELLED,
    OrderState.ERRORED,
    OrderState.EXPIRED,
  ];

  const dataLoader = useRef<DataApi<Order>>(apiClient!.ordersLoader());
  const { data, fetchData, fetchMoreData } = useDataLoader<Order>(dataLoader.current!);

  const getQuery = () => {
    const query: { user_id?: string; order_type?: string; order_state?: string[]; product_symbol?: string } = {};

    if (orderTypeFilter !== 'all') {
      query.order_type = orderTypeFilter;
    }

    if (showUserOrdersOnly) {
      query.user_id = user!.id;
    }

    if (orderStateFilter.length > 1 || (orderStateFilter.length === 1 && orderStateFilter[0] !== 'all')) {
      query.order_state = orderStateFilter;
    }

    if (productSymbol !== 'all') {
      query.product_symbol = productSymbol;
    }

    return query;
  };

  const doFetch = async () => {
    const query = getQuery();
    fetchData(query, '');
  };

  const onOrderTypeFilterChange = (e: SelectChangeEvent<OrderType | 'all'>) => {
    const value = e.target.value as OrderTrackerOrderType;
    handleTabUpdate(index, { orderType: value });
    setNeedsData(true);
  };

  const onOrderStateFilterChange = (e: SelectChangeEvent<OrderTrackerOrderState[]>) => {
    const value = e.target.value as OrderTrackerOrderState[];
    let nextValue = value;

    if (value.length === 0) {
      nextValue = ['all'];
    } else if (orderStateFilter.length === 1 && orderStateFilter[0] === 'all') {
      nextValue = value.filter((v: OrderTrackerOrderState) => v !== 'all');
    } else if (value.includes('all') || value.length === allowedOrderStates.length - 1) {
      nextValue = ['all'];
    } else {
      nextValue = value;
    }

    handleTabUpdate(index, { orderState: nextValue });
    setNeedsData(true);
  };

  const toggleUserFilter = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    handleTabUpdate(index, { showUserOrdersOnly: checked });
    gridApiRef.current?.setColumnsVisible(['user.full_name'], !checked);
    setNeedsData(true);
  };

  const onProductSelected = (p: Product | null) => {
    handleTabUpdate(index, { productSymbol: p ? p.symbol : undefined });
    setNeedsData(true);
  };

  const onTradingAccountSelected = (e: SelectChangeEvent<string>) => {
    const { value } = e.target;
    handleTabUpdate(index, { tradingAccount: value });
  };

  const onRenameTab = (newName: string) => {
    const nameToSet = newName.trim();

    if (nameToSet.length > 0 && nameToSet !== getDefaultTabName(tabData)) {
      updateTab(index, { tabName: newName });
    } else {
      updateTab(index, { tabName: undefined });
    }
  };

  const applyQuickViewToTab = (view: OrderTrackerQuickView) => {
    switch (view) {
      case OrderTrackerQuickView.All:
        updateTab(index, { orderType: 'all', orderState: ['all'] });
        break;
      case OrderTrackerQuickView.Limits:
        updateTab(index, { orderType: OrderType.LIMIT });
        break;
      case OrderTrackerQuickView.Working:
        updateTab(index, { orderState: [OrderState.NEW, OrderState.PARTIALLY_FILLED] });
        break;
      case OrderTrackerQuickView.Completed:
        updateTab(index, { orderState: [OrderState.CANCELLED, OrderState.FILLED, OrderState.ERRORED, OrderState.EXPIRED] });
        break;
    }
    setNeedsData(true);
  };

  const onScrollEnd = (e: BodyScrollEndEvent<Order, any>) => {
    const buffer = 5;
    const lastRowIndex = e.api.getLastDisplayedRowIndex();
    const totalRows = e.api.getDisplayedRowCount();

    if (e.direction === 'vertical' && lastRowIndex >= totalRows - buffer) {
      fetchMoreData();
    }
  };

  useStreamV2Orders(onStreamOrder, gridReady && status === 'connected');

  useEffect(() => {
    if (needsData) {
      doFetch();
    }
    setNeedsData(false);
  }, [needsData]);

  const defaultSelectProps: Pick<SelectProps, 'size' | 'sx'> = { size: 'small' };
  const defaultFormControlProps: Partial<FormControlProps> = { size: 'small', sx: { m: 1, minWidth: 120 }, variant: 'standard' };

  const orderPanelDetails = createOrderPanelDetails(selectedRow);
  const sideBarConfig = createOrderDetailsSidebarConfig(orderPanelDetails, visiblePanel);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', flexGrow: 1 }}>
      {/* Filters */}

      <div style={{ width: '100%', display: 'flex', flexDirection: 'row' }}>
        <FormControl {...defaultFormControlProps}>
          <InputLabel>Order Type</InputLabel>
          <Select value={orderTypeFilter} onChange={onOrderTypeFilterChange} {...defaultSelectProps}>
            {allowedOrderTypes.map(o => {
              return (
                <MenuItem value={o} key={o}>
                  {o.toUpperCase()}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <FormControl {...defaultFormControlProps}>
          <ProductAutocomplete
            onProductSelected={onProductSelected}
            value={selectedProduct}
            sx={{ m: 1, minWidth: 200 }}
            size="small"
            renderOption={(props, option: Product) => {
              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps}>
                  {option.symbol.toUpperCase()}
                </li>
              );
            }}
          />
        </FormControl>

        <FormControl {...defaultFormControlProps}>
          <InputLabel>Order State</InputLabel>
          <Select
            value={orderStateFilter}
            onChange={onOrderStateFilterChange}
            {...defaultSelectProps}
            multiple
            sx={{ ...defaultSelectProps.sx, maxWidth: 240 }}
          >
            {allowedOrderStates.map(o => {
              return (
                <MenuItem value={o} key={o}>
                  {o.toUpperCase()}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <FormControl {...defaultFormControlProps}>
          <InputLabel>Account</InputLabel>
          <Select value={tradingAccount} onChange={onTradingAccountSelected}>
            <MenuItem value={'all'} key={'ALL_ACCOUNTS'}>
              ALL
            </MenuItem>
            {(tradingAccounts || []).map(account => {
              return (
                <MenuItem value={account} key={account}>
                  {account.toUpperCase()}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <div style={{ flexGrow: 1 }}></div>

        <FormControl {...defaultFormControlProps} sx={{ m: 1, minWidth: 200 }}>
          <FormControlLabel
            control={<Checkbox checked={showUserOrdersOnly} onChange={toggleUserFilter} size="small" />}
            label="Only My Orders"
            labelPlacement="start"
          />
        </FormControl>
      </div>

      <StyledBox className="ag-theme-balham-dark custom-grid" sx={{ width: '100%', flexGrow: '1' }}>
        <AgGridReact<Order>
          gridId={id}
          getRowId={v => v.data.id}
          autoSizeStrategy={{ type: 'fitProvidedWidth', width }}
          suppressDragLeaveHidesColumns
          suppressColumnMoveAnimation
          onGridReady={onGridReady}
          onBodyScrollEnd={onScrollEnd}
          columnDefs={columns}
          defaultColDef={defaultColDef}
          containerStyle={{ width }}
          rowData={data}
          isExternalFilterPresent={() => {
            return tradingAccount !== 'all';
          }}
          doesExternalFilterPass={(node: IRowNode<Order>) => node.data?.account_id === tradingAccount}
          onRowClicked={onRowClicked}
          onCellKeyDown={onCellKeyDown}
          onRowDoubleClicked={onRowDoubleClicked}
          sideBar={sideBarConfig}
          onToolPanelVisibleChanged={onToolPanelVisibilityChanged}
        />
      </StyledBox>

      <div style={{ flexShrink: 1, display: 'flex', flexDirection: 'row' }}>
        {/* Tab Name */}
        <FormControl sx={{ width: 300, padding: 0, m: '0 8px 8px 8px' }}>
          <IsolatedTextField
            value={tabName || getDefaultTabName(tabData)}
            onEditFinished={onRenameTab}
            variant="standard"
            sx={{ padding: 0 }}
            label="Tab Name"
          />
        </FormControl>

        {/* Spacer */}
        <div style={{ flexGrow: 1 }} />

        <div style={{ padding: '10px', display: 'flex', gap: 4 }}>
          <Typography variant="body1" sx={{ paddingTop: '.5em', marginRight: '.5em' }}>
            Quick Filters:
          </Typography>
          <Button
            {...QuickButtonDefaultProps}
            onClick={() => {
              applyQuickViewToTab(OrderTrackerQuickView.All);
            }}
          >
            All Orders
          </Button>
          <Button
            {...QuickButtonDefaultProps}
            onClick={() => {
              applyQuickViewToTab(OrderTrackerQuickView.Working);
            }}
          >
            Working
          </Button>
          <Button
            {...QuickButtonDefaultProps}
            onClick={() => {
              applyQuickViewToTab(OrderTrackerQuickView.Completed);
            }}
          >
            Completed
          </Button>
        </div>
      </div>
    </div>
  );
};

export const OrderTrackerTab = (props: OrderTrackerTabProps) => {
  const containerRef = createRef<HTMLDivElement>();
  const [width, setWidth] = useState<number>(-1);
  const measureContainer = () => {
    const newWidth = (containerRef.current?.getBoundingClientRect().width || 600) - 30;
    setWidth(newWidth);
  };

  useEffect(measureContainer, [containerRef]);

  useEffect(() => {
    document.addEventListener('resize', measureContainer);
    return () => {
      document.removeEventListener('resize', measureContainer);
    };
  });

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }} ref={containerRef}>
      <OrderTrackerTabContent {...props} width={width} />
    </div>
  );
};
